// GA4 tracking for the Shop All mobile sort/filter drawer — sort, filter and
// section_click, hooks and event pushes together in one module.
//
// WHY ANY OF THIS IS JAVASCRIPT
// The drawer is not theme markup. It is rendered in the browser by the SearchTap
// Vue bundle (assets/searchtap.js) inside #collectionmodalcontainer, so there is
// no Liquid template to put a class or a data attribute on — and a sort option
// button carries no class at all unless it happens to be the active sort:
//
//   Zi("button", { onClick: e => i.sortList(t.label), value: t.label,
//                  class: K({ "active-sort st-font-medium": t.label === s.sortLabel }) }, ...)
//
// Editing the vendor bundle is not an option either: it is SearchTap's own file
// and gets replaced wholesale on their releases, which would silently take the
// tracking with it. So the hooks are found in the DOM after the SDK draws it and
// re-applied whenever Vue redraws — the same approach searchtap-collection.js
// already takes for every other fix to this drawer.
//
// The filter selection is read from the SDK's own state, with the rendered
// checkboxes as a fallback. The DOM alone is lossy: SearchTap recomputes facet
// values from the current result set, so a selected value that would return no
// results is dropped from the rendered list while the SDK still holds it in
// `selected` — reading only checkboxes silently under-reported the selection.
//
// ─── The hook contract ──────────────────────────────────────────────────────
//   .dnk-section-click  toolbar pill and  data-cta-text = Filter | Sort
//                       the drawer's
//                       inactive tab
//   .dnk-sort-option    a sort option     data-sort-by = its own label
//   .dnk-sort-clear     "Clear sort"      data-cta-text + data-sort-by
//   .dnk-filter-apply   "Done"            data-cta-text + the 4 filter params
//   .dnk-filter-reset   "Reset"           data-cta-text + the 4 filter params
//   .dnk-filter-clear   a group's "Clear" data-cta-text + data-filter-type
//                                         + that group's param only
//   .dnk-drawer-close   the header ✕      data-cta-text + data-drawer-section
//                                         + both events' params
//
// The four filter params are data-product-type, data-skin-concern,
// data-skin-type and data-ingredients, pipe-joined on a multi-select
// ("Combo | Sunscreen"). They match the facet titles the store is indexed with —
// see snippets/st-filter-images.liquid.
//
// Every data-* param is ABSENT rather than empty when it has no value, which is
// how the spec's null is represented. Hence `?? null` on every read below.
//
// MWEB ONLY holds structurally rather than through a viewport check: the toolbar
// pill is hidden on desktop by the SDK's own md:st-hidden lg:st-hidden, the sort
// panel exists only inside the mobile sort component, and the filter hooks are
// stamped only within .mobilesearch. None of these targets is reachable with a
// desktop click.
(() => {
  'use strict';

  const CLIENT_ID_STORAGE_KEY = 'themex-client-id';

  const CONTAINER_ID = 'collectionmodalcontainer';
  const SORT_PANEL_SELECTOR = '.st-sorting-wrapper';
  const SORT_OPTION_SELECTOR = '.list > li';
  const DRAWER_CLASS = 'mobilesearch';
  const DRAWER_OPEN_CLASS = 'open';
  const DRAWER_SELECTOR = `.${DRAWER_CLASS}`;
  const DRAWER_HEADER_SELECTOR = '.filterHeader';
  const FILTER_GROUP_SELECTOR = '.st-widget.st-head-item';
  const TOOLBAR_SELECTOR = '.sortFilterCon';
  const DRAWER_TAB_SELECTOR = '.mobile-filter-title';
  const ACTIVE_TAB_CLASS = 'st-active-bar';

  // The attribute every hook stamps its button/pill label onto (Filter, Sort,
  // Done, Reset, Clear, Close...). Named here once so the eight setParam calls
  // below read as "the CTA label attribute" instead of a repeated bare string.
  // The dataLayer field it ends up in stays cta_text — that's an established
  // GTM/GA4 contract, not just a code name, so it's intentionally not renamed.
  const CTA_TEXT_ATTR = 'data-cta-text';

  // Keyed on the facet title the SDK renders, lowercased. These four are the
  // groups the spec asks for; anything else the dashboard adds later (the price
  // slider, colour) has no parameter of its own and is skipped rather than
  // guessed at.
  const FILTER_GROUP_PARAMS = {
    'product type': 'data-product-type',
    'skin concern': 'data-skin-concern',
    'skin type': 'data-skin-type',
    'ingredients': 'data-ingredients',
  };

  // ─── client_id ────────────────────────────────────────────────────────────
  // Same resolution order as the theme's other tracking files
  // (pair-popup-tracking.js, customers-also-bought-tracking.js,
  // pdp-recently-viewed.js) so client_id agrees across every event: the GA4
  // client id off the _ga cookie, falling back to a self-generated id so a value
  // is always present.
  const generateId = () =>
    crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const getClientId = () => {
    try {
      const gaCookie = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
      if (gaCookie) {
        const parts = decodeURIComponent(gaCookie[1]).split('.');
        if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
      }
    } catch (_) {
      // Malformed _ga cookie value (decodeURIComponent throws on a bad escape
      // sequence) — fall through to the stored/generated id below instead of
      // taking down every pushEvent call on this page.
    }

    try {
      let clientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
      if (!clientId) {
        clientId = generateId();
        localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
      }
      return clientId;
    } catch (_) {
      // localStorage can throw (Safari private mode, quota exceeded, disabled by
      // policy) — fall back to a per-call id rather than letting pushEvent throw.
      return generateId();
    }
  };

  // ─── Reading the drawer ───────────────────────────────────────────────────

  // Writes only on a real change, and treats an empty value as "remove the
  // attribute" — an absent param is how the spec's null is represented.
  const setParam = (element, name, value) => {
    if (!value) {
      if (element.hasAttribute(name)) element.removeAttribute(name);
      return;
    }
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  };

  const readSortOptionLabel = (option) =>
    option.querySelector('.sortByValues')?.textContent.trim() || null;

  let lastKnownSort = null;

  // The sort panel is v-if="showMobileSort", so it does not exist at all while
  // the drawer sits on the Filters tab — and the ✕ is stamped in both states.
  // Remembering the last value seen covers that gap, with the sidebar's sort box
  // (which searchtap-collection.js keeps in sync) as a final fallback for a first
  // read that happens before the panel has ever been opened.
  const getAppliedSort = () => {
    for (const option of document.querySelectorAll(
      `${SORT_PANEL_SELECTOR} ${SORT_OPTION_SELECTOR}`
    )) {
      if (!option.querySelector('button.active-sort')) continue;

      const label = readSortOptionLabel(option);
      if (label) {
        lastKnownSort = label;
        return label;
      }
    }

    if (lastKnownSort) return lastKnownSort;

    return document.querySelector('.st-sort-box .st-tracking-normal')?.textContent.trim() || null;
  };

  // The group heading also holds that group's "Clear" control and a chevron, so
  // only its own text nodes carry the title.
  const readGroupTitle = (widget) => {
    const heading = widget?.querySelector('.st-widget-title');
    if (!heading) return null;

    const title = [...heading.childNodes]
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent.trim())
      .join(' ')
      .trim();

    return title || null;
  };

  // Walks the Vue tree for the SearchPage instance, the same way
  // searchtap-collection.js does. Its `filters` array is what the SDK builds each
  // search request from, and the drawer's checkboxes v-model straight into it, so
  // `selected` is both authoritative and current the moment a chip is tapped.
  const getSearchPageProxy = () => {
    const rootVNode = document.getElementById(CONTAINER_ID)?._vnode;
    if (!rootVNode?.component) return null;

    const walk = (vnode) => {
      if (!vnode || typeof vnode !== 'object') return null;

      if (vnode.component) {
        if (vnode.component.type?.name === 'SearchPage') return vnode.component.proxy;
        const found = walk(vnode.component.subTree);
        if (found) return found;
      }

      if (Array.isArray(vnode.children)) {
        for (const child of vnode.children) {
          const found = walk(child);
          if (found) return found;
        }
      }

      return null;
    };

    return walk(rootVNode.component.subTree);
  };

  const collectFromSdk = (filters) => {
    const selections = new Map();

    for (const filter of filters) {
      const attribute = FILTER_GROUP_PARAMS[filter.title?.trim().toLowerCase()];
      if (!attribute) continue;

      const values = (filter.selected ?? [])
        .map((value) => String(value).trim())
        .filter(Boolean);

      if (values.length) selections.set(attribute, values.join(' | '));
    }

    return selections;
  };

  const collectFromDom = () => {
    // Unioned across every widget carrying the same facet title rather than
    // assigned, because one facet is rendered more than once: the drawer shows an
    // image-chip row (hidden checkboxes inside .filter-label, driven by
    // window.filterImages) as well as a plain list, and the desktop sidebar
    // renders its own copy. Assigning per widget let whichever was processed last
    // drop the values found in the others.
    const grouped = new Map();

    for (const widget of document.querySelectorAll(FILTER_GROUP_SELECTOR)) {
      const attribute = FILTER_GROUP_PARAMS[readGroupTitle(widget)?.toLowerCase()];
      if (!attribute) continue;

      if (!grouped.has(attribute)) grouped.set(attribute, new Set());
      const values = grouped.get(attribute);

      for (const input of widget.querySelectorAll('input[type="checkbox"]')) {
        if (!input.checked) continue;
        const value = input.value.trim();
        if (value) values.add(value);
      }
    }

    const selections = new Map();
    for (const [attribute, values] of grouped) {
      if (values.size) selections.set(attribute, [...values].join(' | '));
    }

    return selections;
  };

  // Returns a Map of param attribute -> pipe-joined selected values, holding only
  // the groups that have a selection.
  const getSelectedFilters = () => {
    const filters = getSearchPageProxy()?.filters;
    if (filters?.length) return collectFromSdk(filters);

    return collectFromDom();
  };

  // Stamps all four filter params. onlyAttribute limits it to a single group,
  // which is what a per-group "Clear" reports.
  const setFilterParams = (element, selections, onlyAttribute = null) => {
    for (const attribute of Object.values(FILTER_GROUP_PARAMS)) {
      const isWanted = onlyAttribute === null || attribute === onlyAttribute;
      setParam(element, attribute, isWanted ? selections.get(attribute) : null);
    }
  };

  // ─── Stamping the hooks ───────────────────────────────────────────────────

  const stampSortOptions = (appliedSort) => {
    for (const panel of document.querySelectorAll(SORT_PANEL_SELECTOR)) {
      const options = [...panel.querySelectorAll(SORT_OPTION_SELECTOR)];

      options.forEach((option, index) => {
        const button = option.querySelector('button');
        if (!button) return;

        // The last option is not a sort value at all: searchtap.js's own
        // stylesheet collapses its label (`.list li:last-child span
        // { font-size: 0 }`) and paints "Clear Sort" over it with a ::after, so
        // it reads and behaves as a clear control even though what it applies
        // underneath is the default sort. Hence data-sort-by carries the applied
        // sort here rather than this option's own label, per the spec. Guarded on
        // there being more than one option so a single-option list is never
        // mislabelled as clear.
        const isClear = options.length > 1 && index === options.length - 1;

        button.classList.add('dnk-sort-option');
        button.classList.toggle('dnk-sort-clear', isClear);

        setParam(button, CTA_TEXT_ATTR, isClear ? 'Clear sort' : null);
        setParam(button, 'data-sort-by', isClear ? appliedSort : readSortOptionLabel(option));
      });
    }
  };

  const stampDrawerHeader = (appliedSort, selections) => {
    for (const header of document.querySelectorAll(DRAWER_HEADER_SELECTOR)) {
      // Matched on each tab's own text rather than its position, the way
      // searchtap-collection.js identifies these two.
      const tabs = [...header.querySelectorAll(DRAWER_TAB_SELECTOR)];
      const isSortTab = (tab) => tab.textContent.trim().toLowerCase().includes('sort');

      // Switching tabs inside an open drawer is a section_click too. The labels
      // read "Filters" and "Sort By", but they are reported as Filter / Sort so
      // the parameter keeps the two values the spec allows and a tab agrees with
      // the bottom pill.
      //
      // Only the INACTIVE tab carries the hook: tapping the tab that is already
      // active changes nothing, and the spec asks for the section the user moved
      // to. Vue re-renders on a switch and the observer re-stamps, so the hook
      // moves to the other tab straight after — the click itself has already
      // fired synchronously by then.
      for (const tab of tabs) {
        tab.classList.toggle('dnk-section-click', !tab.classList.contains(ACTIVE_TAB_CLASS));
        setParam(tab, CTA_TEXT_ATTR, isSortTab(tab) ? 'Sort' : 'Filter');
      }

      const close = header.querySelector('.st-pull-right');
      if (!close) continue;

      // Which tab the drawer is on decides which event a ✕ click belongs to:
      // 'Close' is a cta_text in the sort spec and the filter spec both, and this
      // is the same element for each.
      const activeTab = tabs.find((tab) => tab.classList.contains(ACTIVE_TAB_CLASS));
      const onSortTab = Boolean(activeTab && isSortTab(activeTab));

      close.classList.add('dnk-drawer-close');
      setParam(close, CTA_TEXT_ATTR, 'Close');
      setParam(close, 'data-drawer-section', onSortTab ? 'sort' : 'filter');
      setParam(close, 'data-sort-by', appliedSort);
      setFilterParams(close, selections);
    }
  };

  // Scoped to the drawer because .filter-clear and the filter groups are shared
  // with the desktop sidebar, and these events are mweb-only. .apply-btn and
  // .st-reset-all-mobile are drawer-only already; scoping them too keeps it
  // uniform.
  const stampFilterControls = (selections) => {
    for (const drawer of document.querySelectorAll(DRAWER_SELECTOR)) {
      // "Done" and "Reset" both report the whole current selection — the spec
      // wants the applied filters on a Reset click, not the empty state it
      // leaves behind.
      const done = drawer.querySelector('.apply-btn');
      if (done) {
        done.classList.add('dnk-filter-apply');
        setParam(done, CTA_TEXT_ATTR, 'Done');
        setFilterParams(done, selections);
      }

      const reset = drawer.querySelector('.st-reset-all-mobile');
      if (reset) {
        reset.classList.add('dnk-filter-reset');
        setParam(reset, CTA_TEXT_ATTR, 'Reset');
        setFilterParams(reset, selections);
      }

      // A per-group "Clear" reports only its own group, the other three staying
      // null. data-filter-type carries the group's title as well, because the
      // spec's "pass the filter type for which the clear was clicked" reads
      // either as the type's name or as that type's values — this covers both.
      // The price-slider group has no parameter of its own, so its Clear gets the
      // cta_text and the title, and no values.
      for (const clear of drawer.querySelectorAll('.filter-clear')) {
        const title = readGroupTitle(clear.closest('.st-widget'));
        const attribute = FILTER_GROUP_PARAMS[title?.toLowerCase()] ?? null;

        clear.classList.add('dnk-filter-clear');
        setParam(clear, CTA_TEXT_ATTR, 'Clear');
        setParam(clear, 'data-filter-type', title);
        setFilterParams(clear, selections, attribute);
      }
    }
  };

  // The toolbar is one clickable pill with .sort_h nested INSIDE .filter_h, so a
  // tap on "Sort" hits both. Both carry the hook and the event reads
  // closest('.dnk-section-click'), which resolves to the innermost match — that
  // is the Sort/Filter split the spec asks for, for free.
  //
  // Both labels are hardcoded rather than scraped, so they are immune to the
  // SDK's own "FIlter" typo (which searchtap-collection.js only repairs after a
  // repaint).
  const stampSectionClickPills = () => {
    for (const pill of document.querySelectorAll(`${TOOLBAR_SELECTOR} .filter_h`)) {
      pill.classList.add('dnk-section-click');
      setParam(pill, CTA_TEXT_ATTR, 'Filter');
    }

    for (const sortPill of document.querySelectorAll(`${TOOLBAR_SELECTOR} .sort_h`)) {
      sortPill.classList.add('dnk-section-click');
      setParam(sortPill, CTA_TEXT_ATTR, 'Sort');
    }
  };

  let isStampScheduled = false;

  const stampAll = () => {
    isStampScheduled = false;

    const appliedSort = getAppliedSort();
    const selections = getSelectedFilters();

    stampSortOptions(appliedSort);
    stampDrawerHeader(appliedSort, selections);
    stampFilterControls(selections);
    stampSectionClickPills();
  };

  // One stamp per frame at most. requestAnimationFrame also lands after Vue has
  // flushed its patch queue (which runs in a microtask), so a re-render caused by
  // the same interaction is already in the DOM by the time this reads it.
  const scheduleStamp = () => {
    if (isStampScheduled) return;
    isStampScheduled = true;
    requestAnimationFrame(stampAll);
  };

  // ─── Event pushes ─────────────────────────────────────────────────────────

  const pushEvent = (payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ...payload, client_id: getClientId() });
  };

  const filterParamsPayload = (selections) => ({
    product_type: selections.get('data-product-type') ?? null,
    skin_concern: selections.get('data-skin-concern') ?? null,
    skin_type: selections.get('data-skin-type') ?? null,
    ingredients: selections.get('data-ingredients') ?? null,
  });

  // ─── Filter changes the four CTAs never get to report ─────────────────────
  // The drawer does not always close through Done / Reset / Clear / ✕. Choosing a
  // sort value closes it (patchCloseDrawerOnSortSelect in
  // searchtap-collection.js), so a shopper can tick filters on the Filters tab,
  // switch to Sort By, pick a sort, and walk away with filters applied that no
  // `filter` event ever reported.
  //
  // hasUntrackedFilterChange is set only by a real `change` on a filter checkbox
  // and cleared the moment any filter CTA reports it, which keeps the two paths
  // from both firing. Clearing and resetting move the checkboxes programmatically
  // and Vue dispatches no `change` for that, so those stay on their CTA alone.
  let hasUntrackedFilterChange = false;

  const reportUntrackedFilterChange = () => {
    if (!hasUntrackedFilterChange) return;
    hasUntrackedFilterChange = false;

    // cta_text is null because no named button was clicked — the same convention
    // the sort spec uses for a value picked without a CTA.
    pushEvent({
      event: 'shop_all_filter',
      cta_text: null,
      ...filterParamsPayload(getSelectedFilters()),
    });
  };

  // For a plain sort option the value is that option's own label; for "Clear
  // sort" and the ✕ the spec asks for the sort currently applied. The stamp holds
  // whichever of those is right, so these are fallbacks only.
  const readSortBy = (target) => {
    if (target.dataset.sortBy) return target.dataset.sortBy;

    const isPlainOption =
      target.classList.contains('dnk-sort-option') &&
      !target.classList.contains('dnk-sort-clear');

    if (isPlainOption) return readSortOptionLabel(target);

    return getAppliedSort();
  };

  // The ✕ carries both events' hooks, so each listener bows out when the drawer
  // is on the other tab.
  const isForeignCloseClick = (target, section) =>
    target.classList.contains('dnk-drawer-close') &&
    target.dataset.drawerSection !== section;

  // All three listen in the CAPTURE phase. A group's "Clear" is bound by the SDK
  // as withModifiers(..., ["stop"]) — it calls stopPropagation() — so a
  // bubble-phase listener on document would never see that click at all.
  // patchFilterClearButton() and initSelectedFilterRemoveSync() in
  // searchtap-collection.js capture for the same reason.
  const bindEvents = () => {
    document.addEventListener(
      'click',
      (event) => {
        const pill = event.target.closest?.('.dnk-section-click');
        if (!pill) return;

        // searchtap-collection.js drives these tabs programmatically in two
        // places — patchMobileSortTabOpen() clicks the Sort By tab after the
        // bottom Sort pill is tapped, and resetMobileSortTab() clicks the Filters
        // tab on every drawer close. Both would otherwise report a section_click
        // the shopper never made: the first as a duplicate 'Sort', the second as
        // a phantom 'Filter' on close. isTrusted is false for element.click() and
        // true for a real tap (keyboard activation included).
        if (!event.isTrusted) return;

        pushEvent({
          event: 'shop_all_section_click',
          cta_text: pill.dataset.ctaText ?? null,
        });
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest?.('.dnk-sort-option, .dnk-drawer-close');
        if (!target) return;
        if (isForeignCloseClick(target, 'sort')) return;

        pushEvent({
          event: 'shop_all_sort',
          cta_text: target.dataset.ctaText ?? null,
          sort_by: readSortBy(target),
        });
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest?.(
          '.dnk-filter-apply, .dnk-filter-reset, .dnk-filter-clear, .dnk-drawer-close'
        );
        if (!target) return;
        if (isForeignCloseClick(target, 'filter')) return;

        // This click reports the change, so the drawer's close edge must not
        // report it a second time.
        hasUntrackedFilterChange = false;

        pushEvent({
          event: 'shop_all_filter',
          cta_text: target.dataset.ctaText ?? null,
          product_type: target.dataset.productType ?? null,
          skin_concern: target.dataset.skinConcern ?? null,
          skin_type: target.dataset.skinType ?? null,
          ingredients: target.dataset.ingredients ?? null,
        });
      },
      true
    );
  };

  // ─── Init ─────────────────────────────────────────────────────────────────

  // Re-stamping cannot feed its own observer: classList.add of a token already
  // present and setParam of an unchanged value both leave the attribute
  // untouched, so neither produces a mutation record — and the data-* writes are
  // outside attributeFilter regardless.
  const init = () => {
    const root = document.getElementById(CONTAINER_ID);
    if (!root) return;

    bindEvents();

    // Two separate reasons to re-stamp: Vue owns `class` on every one of these
    // elements and overwrites it on each patch, and the sort panel is v-if'd, so
    // it is torn down and rebuilt on every Filters <-> Sort By switch.
    // The same observer also watches the drawer's own `open` class for the close
    // edge, which is where a filter change that no CTA reported gets flushed.
    // attributeOldValue carries the previous class list, the only state needed to
    // spot that transition — and only attribute records are considered, since a
    // childList record on an already-open drawer would otherwise read as a fresh
    // open.
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes') continue;

        const { target, oldValue } = mutation;
        if (!target.classList?.contains(DRAWER_CLASS)) continue;

        const wasOpen = (oldValue ?? '').split(/\s+/).includes(DRAWER_OPEN_CLASS);
        const isOpen = target.classList.contains(DRAWER_OPEN_CLASS);
        if (wasOpen && !isOpen) reportUntrackedFilterChange();
      }

      scheduleStamp();
    }).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    });

    // Ticking a filter checkbox changes only the input's `checked` property,
    // which produces no mutation record, and the SDK's re-render lands a request
    // later — so a shopper who taps a checkbox and then "Done" straight away
    // would otherwise be reported with the previous selection.
    //
    // Restricted to checkboxes so the price slider, which has no parameter of its
    // own, cannot flag a change that would then report as four nulls.
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!input.closest?.(DRAWER_SELECTOR)) return;

      if (input.type === 'checkbox') hasUntrackedFilterChange = true;
      scheduleStamp();
    });

    scheduleStamp();
  };

  // Exposed for debugging and for GTM, if it would rather compute a parameter
  // itself than read the stamped attribute.
  window.dnkShopallDrawerTracking = {
    getClientId,
    getAppliedSort,
    getSelectedFilters,
    refresh: scheduleStamp,
  };

  document.addEventListener('DOMContentLoaded', init);
})();